import { useState, useMemo, useRef, useEffect } from 'react';
import { Space, createStyles, Text, Button, Divider } from '@mantine/core';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Label } from 'recharts';
import { prepareChartDataFromCache } from '../../assets/constants';
import { addCommasToNumber } from '../../assets/constants';
import { useResize } from '../../assets/custom-hooks/resize';

const useStyles = createStyles((theme) => ({

    responseContainer: {
        background: 'white',
        border: `1px solid #ced4da`,
        padding: '10px',
        borderRadius: theme.radius.md,
        paddingLeft: `calc(2.625rem  / 3)`,
        paddingRight: `calc(2.625rem  / 3)`,
        paddingTop: `0.625rem`,
        paddingBottom: `0.625rem`,
        pointerEvents: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        fontSize: '16px',
    },

    playButton: {
        float: 'right',
    }

}));

function ChangeHistoryVisual({ questionHistory, questionText, questionId, isLast }) {

    const [currentText, setCurrentText] = useState(questionHistory[questionHistory.length - 1].state);
    const [currentIndex, setCurrentIndex] = useState(questionHistory.length - 1);
    const [isRunningVisualization, setIsRunningVisualization] = useState(false);
    const [responseContainerHeight, setResponseContainerHeight] = useState(0);
    const responseRef = useRef(null);
    const { width } = useResize(responseRef);
    const { classes } = useStyles();

    const chartData = useMemo(() => prepareChartDataFromCache(questionHistory), [questionHistory]);
    const finalResponse = useMemo(() => questionHistory[questionHistory.length - 1].state, [questionHistory]);
    const maxKeystrokeTime = useMemo(() => Math.max(...chartData.map(entry => entry.keystroke_time)), [chartData]);
    const extraPadding = useMemo(() => maxKeystrokeTime > 800 && maxKeystrokeTime < 8000, [maxKeystrokeTime]);

    useEffect(() => {
        const height = responseRef.current.clientHeight;
        setResponseContainerHeight(height);
    }, [finalResponse]);

    const startVisualization = () => {
        setIsRunningVisualization(true);
        const timeouts = questionHistory.map((change, index) => {
            return setTimeout(() => {
                setCurrentText(change.state);
                setCurrentIndex(index);
                if (index === questionHistory.length - 1) {
                    setIsRunningVisualization(false);
                }
            }, change.time_since_start );
        });

        // Clear timeouts on component unmount or re-run
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
            setIsRunningVisualization(false);
        };
    };

    const CustomDot = ({ cx, cy, index }) => {
        if (!isRunningVisualization) return <circle cx={cx} cy={cy} r={3} fill="#333" />;
        if (index < currentIndex) {
            return <circle cx={cx} cy={cy} r={3} fill="#333" />;
        } else if (index === currentIndex) {
            return (
                <>
                    <circle cx={cx} cy={cy} r={3} fill="#333" />
                    <circle cx={cx} cy={cy} r={8} fill="rgba(0,0,0,0.15)" />
                </>
            );
        } else {
            return <circle cx={cx} cy={cy} r={3} fill="#ffffff" stroke="#333" />;
        }
    };

    return (
        <>
            <Divider
                my="0"
                label={questionId}
                labelPosition="center"
                labelProps={{ size: 'sm' }}
            />
            <Space h='md' />
            <Text size='md' weight={500}>
                {questionText}
            </Text>
            <Text size="lg">
                <div
                    ref={responseRef}
                    style={{ minHeight: `${responseContainerHeight}px` }}
                    className={classes.responseContainer}
                >
                    {currentText}
                </div>
            </Text>
            <Space h='xs' />
            <Button
                onClick={startVisualization}
                disabled={isRunningVisualization}
                radius='md'
                className={classes.playButton}
            >
                Play history
            </Button>
            <Space h='xl' />
            <LineChart
                width={width}
                height={300}
                data={chartData}
                margin={{ top: 25, right: 20, left: extraPadding ? 30 : 20, bottom: 20 }}
            >
                <CartesianGrid strokeDasharray="5 5" stroke="#eee" />
                <Line
                    type="monotone"
                    dataKey="keystroke_time"
                    stroke="#333"
                    dot={<CustomDot />}
                    animationDuration={1000}
                />
                <XAxis
                    dataKey="index"
                    interval={chartData.length < 10 ? 1 : Math.round(chartData.length / 10)}
                >
                    {
                        maxKeystrokeTime < 8000 &&
                        <Label
                            value="Keystroke index"
                            dy={25}
                            fill='#333'
                        />
                    }
                </XAxis>
                <YAxis tickFormatter={addCommasToNumber}>
                    {
                        maxKeystrokeTime < 8000 &&
                        <Label
                            value="Keystroke time (ms)"
                            angle={-90}
                            dx={extraPadding ? -41 : -27}
                            fill='#333'
                        />
                    }
                </YAxis>
            </LineChart>
            <Space h={isLast ? 'xl' : 45} />
        </>
    );
}

export default ChangeHistoryVisual;